// ─── Template: kinetic-title-v1 ────────────────────────────────────────────
// A bold title with an optional subtitle and an animated accent bar.
// Time-deterministic: the entire frame is a pure function of `frame`.

import React from 'react';
import { AbsoluteFill, Easing, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import type { AnimationTemplate } from '../types';

// Named presets so the form stays simple and the values reproducible. The
// actual numbers live in the lookup tables below — UI/agent only deal with
// the named keys.
export type SpeedPreset = 'lento' | 'normal' | 'rapido';
export type AccentWidth = 'sm' | 'md' | 'lg' | 'none';
export type AlignPreset = 'left' | 'center' | 'right';

// Frames the title/bar/subtitle take to fully reveal at each speed.
// `normal` matches the original (hand-tuned) timings; the others scale them.
const SPEED_MULTIPLIER: Record<SpeedPreset, number> = {
  lento: 1.6,
  normal: 1,
  rapido: 0.6,
};

const ACCENT_WIDTH_PX: Record<AccentWidth, number> = {
  none: 0,
  sm: 140,
  md: 240,
  lg: 360,
};

// Common font stack so unknown families still fall back gracefully.
const FONT_STACK = 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const flexAlign = (a: AlignPreset): React.CSSProperties['alignItems'] =>
  a === 'left' ? 'flex-start' : a === 'right' ? 'flex-end' : 'center';

export type KineticTitleProps = {
  // Content
  title: string;
  subtitle: string;
  // Title styling
  titleFont: string;
  /** Title font size as % of composition height (matches text clips). */
  titleSize: number;
  titleColor: string;
  titleBold: boolean;
  titleItalic: boolean;
  titleAlign: AlignPreset;
  // Subtitle styling
  subtitleFont: string;
  /** Subtitle font size as % of composition height. */
  subtitleSize: number;
  subtitleColor: string;
  subtitleBold: boolean;
  subtitleItalic: boolean;
  subtitleAlign: AlignPreset;
  // Scene styling. `background` accepts the literal value `'transparent'`
  // (selectable from the color picker) to render the animation as an
  // overlay on top of the clip below.
  background: string;
  accent: string;
  accentWidth: AccentWidth;
  speed: SpeedPreset;
};

const Component: React.FC<KineticTitleProps> = ({
  title, subtitle,
  titleFont, titleSize, titleColor, titleBold, titleItalic, titleAlign,
  subtitleFont, subtitleSize, subtitleColor, subtitleBold, subtitleItalic, subtitleAlign,
  background, accent, accentWidth, speed,
}) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const k = SPEED_MULTIPLIER[speed] ?? 1;
  const targetBar = ACCENT_WIDTH_PX[accentWidth] ?? ACCENT_WIDTH_PX.md;
  // Sizes are stored as % del lado más corto de la composición. Usamos
  // `min(width, height)` para que el mismo % se vea igual en horizontal
  // (16:9), vertical (9:16) y cuadrado/4:3 — si usáramos solo `height`
  // las letras explotan de tamaño en formato reel.
  const refDim = Math.min(width, height);
  const titlePx = (titleSize / 100) * refDim;
  const subtitlePx = (subtitleSize / 100) * refDim;

  // All keyframes scale by `k` so 'lento' stretches and 'rapido' compresses.
  const barWidth = interpolate(frame, [0, 22 * k], [0, targetBar], {
    easing: Easing.out(Easing.cubic),
    extrapolateRight: 'clamp',
  });
  const titleY = interpolate(frame, [4 * k, 22 * k], [60, 0], {
    easing: Easing.out(Easing.cubic),
    extrapolateRight: 'clamp',
  });
  const titleOpacity = interpolate(frame, [4 * k, 18 * k], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const subY = interpolate(frame, [14 * k, 32 * k], [40, 0], {
    easing: Easing.out(Easing.cubic),
    extrapolateRight: 'clamp',
  });
  const subOpacity = interpolate(frame, [14 * k, 28 * k], [0, 0.78], {
    extrapolateRight: 'clamp',
  });

  // Each text aligns independently. We center-align the column to keep the
  // accent bar and the chosen text alignment visually consistent — text
  // alignment moves the line within its block, while the column stays
  // centered in the frame.
  return (
    <AbsoluteFill
      style={{
        background,
        alignItems: 'stretch',
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '0 6%',
        fontFamily: FONT_STACK,
      }}
    >
      {accentWidth !== 'none' && (
        <div
          style={{
            width: barWidth,
            height: 6,
            background: accent,
            borderRadius: 3,
            marginBottom: 32,
            alignSelf: flexAlign(titleAlign),
          }}
        />
      )}
      <h1
        style={{
          margin: 0,
          color: titleColor,
          fontSize: titlePx,
          fontFamily: `${titleFont}, ${FONT_STACK}`,
          fontWeight: titleBold ? 800 : 500,
          fontStyle: titleItalic ? 'italic' : 'normal',
          lineHeight: 1.05,
          letterSpacing: -1,
          textAlign: titleAlign,
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
        }}
      >
        {title}
      </h1>
      {subtitle ? (
        <p
          style={{
            margin: '28px 0 0',
            color: subtitleColor,
            fontSize: subtitlePx,
            fontFamily: `${subtitleFont}, ${FONT_STACK}`,
            fontWeight: subtitleBold ? 700 : 400,
            fontStyle: subtitleItalic ? 'italic' : 'normal',
            textAlign: subtitleAlign,
            opacity: subOpacity,
            transform: `translateY(${subY}px)`,
          }}
        >
          {subtitle}
        </p>
      ) : null}
    </AbsoluteFill>
  );
};

// Reused option lists — keeps the schema readable.
const ALIGN_OPTIONS = [
  { value: 'left',   label: 'Izquierda' },
  { value: 'center', label: 'Centro' },
  { value: 'right',  label: 'Derecha' },
];

export const kineticTitleTemplate: AnimationTemplate<KineticTitleProps> = {
  id: 'kinetic-title-v1',
  label: 'Título dinámico',
  description: 'Título con subtítulo y barra de acento que entran animados.',
  category: 'text',
  defaultDurationSec: 3,
  defaultProps: {
    title: 'Lanzamiento Q3',
    subtitle: 'Disponible ahora',
    titleFont: 'Inter',
    titleSize: 8,
    titleColor: '#FFFFFF',
    titleBold: true,
    titleItalic: false,
    titleAlign: 'center',
    subtitleFont: 'Inter',
    subtitleSize: 3,
    subtitleColor: '#FFFFFF',
    subtitleBold: false,
    subtitleItalic: false,
    subtitleAlign: 'center',
    background: '#0B0F1A',
    accent: '#1644EB',
    accentWidth: 'md',
    speed: 'normal',
  },
  // The schema drives the editor form. Per-text style props are LINKED via
  // the parent text's `styling` table — they don't render at the top level,
  // they live inside the per-text style popover. `agentExposed: true` flags
  // the props we'll surface to the agent's tool schema; everything else
  // stays user-only and inherits defaults when the agent creates a clip.
  propsSchema: {
    // ── Content (top-level rows) ─────────────────────────────────────────
    title: {
      type: 'text', label: 'Título', placeholder: 'Tu título', agentExposed: true,
      styling: {
        font: 'titleFont', size: 'titleSize', color: 'titleColor',
        bold: 'titleBold', italic: 'titleItalic', align: 'titleAlign',
      },
    },
    subtitle: {
      type: 'text', label: 'Subtítulo', placeholder: 'Texto de apoyo (opcional)', agentExposed: true,
      styling: {
        font: 'subtitleFont', size: 'subtitleSize', color: 'subtitleColor',
        bold: 'subtitleBold', italic: 'subtitleItalic', align: 'subtitleAlign',
      },
    },

    // ── Title style props (linked, hidden from top-level) ────────────────
    titleFont:   { type: 'font',    label: 'Tipografía' },
    titleSize:   { type: 'number',  label: 'Tamaño', min: 2, max: 30, step: 0.5, decimals: 1, unit: '%' },
    titleColor:  { type: 'color',   label: 'Color' },
    titleBold:   { type: 'boolean', label: 'Negrita',    glyph: 'B' },
    titleItalic: { type: 'boolean', label: 'Cursiva',    glyph: 'I' },
    titleAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },

    // ── Subtitle style props (linked, hidden from top-level) ─────────────
    subtitleFont:   { type: 'font',    label: 'Tipografía' },
    subtitleSize:   { type: 'number',  label: 'Tamaño', min: 2, max: 20, step: 0.5, decimals: 1, unit: '%' },
    subtitleColor:  { type: 'color',   label: 'Color' },
    subtitleBold:   { type: 'boolean', label: 'Negrita',    glyph: 'B' },
    subtitleItalic: { type: 'boolean', label: 'Cursiva',    glyph: 'I' },
    subtitleAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },

    // ── Scene props (top-level rows) ─────────────────────────────────────
    background:  { type: 'color', label: 'Fondo', allowTransparent: true },
    accent:      { type: 'color',  label: 'Color de acento' },
    accentWidth: {
      type: 'select', label: 'Barra de acento',
      options: [
        { value: 'none', label: 'Sin barra' },
        { value: 'sm',   label: 'Corta' },
        { value: 'md',   label: 'Media' },
        { value: 'lg',   label: 'Larga' },
      ],
    },
    speed: {
      type: 'select', label: 'Velocidad',
      options: [
        { value: 'lento',  label: 'Lento' },
        { value: 'normal', label: 'Normal' },
        { value: 'rapido', label: 'Rápido' },
      ],
    },
  },
  Component,
};
